// @flow

/**
 * `html-to-image` builds its own font CSS by walking `document.styleSheets`.
 * That walk is fragile: cross origin sheets throw on `cssRules`, and the
 * fallback path inserts the fetched rules one by one without guarding each
 * insert, so a single rule Chrome refuses to parse (`::-moz-focus-inner`, from
 * a dependency's stylesheet) aborts the rest, `@font-face` included.
 *
 * Passing `fontEmbedCSS` skips that walk entirely, so we build the CSS here:
 * fetch the faces we need and inline the font files as `data:` URLs, the same
 * way the images inside the capture are inlined.
 */

// Weights used by the receipt and the shipping label, matching the theme font.
const FONT_CSS_URL =
  'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap';

// Poppins also ships devanagari. Keeping only the faces that cover basic latin
// avoids embedding a few hundred KB no receipt will ever render.
const LATIN_UNICODE_RANGE = 'u+0000-00ff';

const FONT_FILE_RE = /https:\/\/fonts\.gstatic\.com[^)"']+/g;

let fontEmbedCSSPromise: Promise<string> | null = null;

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onloadend = () => resolve(String(reader.result));
    reader.readAsDataURL(blob);
  });
}

function keepLatinFaces(cssText: string): string {
  const faces = cssText.match(/@font-face\s*{[^}]*}/g) || [];

  return faces
    .filter(face => {
      const range = face.match(/unicode-range:\s*([^;]+);/i);
      return !range || range[1].toLowerCase().includes(LATIN_UNICODE_RANGE);
    })
    .join('\n');
}

async function inlineFontFiles(cssText: string): Promise<string> {
  const urls = [...new Set(cssText.match(FONT_FILE_RE) || [])];

  const inlined = await Promise.all(
    urls.map(async url => {
      try {
        const res = await fetch(url);
        if (!res.ok)
          throw new Error(`Request to "${url}" failed: ${res.status}`);
        return [url, await blobToDataUrl(await res.blob())];
      } catch (err) {
        console.log(err);
        return [url, null];
      }
    })
  );

  return inlined.reduce(
    (css, [url, dataUrl]) => (dataUrl ? css.split(url).join(dataUrl) : css),
    cssText
  );
}

/**
 * CSS to hand to `html-to-image` as `fontEmbedCSS`. Resolved once and reused,
 * since the faces are the same for every capture.
 *
 * Returns an empty string if the fonts cannot be fetched: that still skips the
 * stylesheet walk, so the capture falls back to a system font instead of
 * failing.
 */
export function getFontEmbedCSS(): Promise<string> {
  if (!fontEmbedCSSPromise) {
    fontEmbedCSSPromise = fetch(FONT_CSS_URL)
      .then(res => res.text())
      .then(keepLatinFaces)
      .then(inlineFontFiles)
      .catch(err => {
        console.log(err);
        return '';
      });
  }

  return fontEmbedCSSPromise;
}
